import React, { useCallback, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { bool, number, string } from 'prop-types';
import classNames from 'classnames';
import { IconArrowHead } from '..';

import css from './Carousel.module.css';

const nextElement = el => {
  if (el.nextElementSibling) {
    return el.nextElementSibling;
  } else {
    return el.parentNode.firstElementChild;
  }
};

const prevElement = el => {
  if (el.previousElementSibling) {
    return el.previousElementSibling;
  } else {
    return el.parentNode.lastElementChild;
  }
};

const Carousel = forwardRef((props, ref) => {
  const {
    rootClassName,
    className,
    contentClassName,
    children,
  columns,
    useSlider,
    extended,
    showArrows = true, // New prop to control arrow visibility
  responsive,
  } = props;

  const carouselRef = useRef(null);
  // Determine active columns (supports responsive breakpoints: keys interpreted as min-width px)
  const [activeColumns, setActiveColumns] = useState(columns || 1);

  useEffect(() => {
    if (!responsive || typeof window === 'undefined') {
      setActiveColumns(columns || 1);
      return;
    }

    const parse = () => {
      const width = window.innerWidth;
      // Treat keys as max-width breakpoints (mobile-first ascending)
      const points = Object.keys(responsive)
        .map(k => parseInt(k, 10))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b); // ascending

      // Find first breakpoint whose max-width is >= current width
      for (let i = 0; i < points.length; i++) {
        const bp = points[i];
        if (width <= bp) {
          const cfg = responsive[bp];
          if (cfg && cfg.columns) {
            const candidate = cfg.columns;
            setActiveColumns(Math.min(candidate, children.length || candidate));
            return;
          }
        }
      }

      // If wider than all breakpoints, use the largest defined breakpoint columns
      const largest = points[points.length - 1];
      if (largest && responsive[largest]?.columns) {
  const candidate = responsive[largest].columns;
  setActiveColumns(Math.min(candidate, children.length || candidate));
      } else {
        setActiveColumns(columns || 1);
      }
    };

    parse();
    window.addEventListener('resize', parse);
    return () => window.removeEventListener('resize', parse);
  }, [responsive, columns, children.length]);

  const isSliderEnable = useMemo(() => useSlider && (activeColumns && children.length > activeColumns), [
    children.length,
    activeColumns,
    useSlider,
  ]);

  const [isReversingClass, itemClass, carouselClass] = useMemo(() => {
    return activeColumns % 3 === 0
      ? [css.isReversing, css.item, css.carousel]
      : [css.isReversing4, css.item4, css.carousel4];
  }, [activeColumns]);

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, { [css.extended]: extended }, className);

  const onClickNext = useCallback(() => {
    carouselRef.current.classList.add(isReversingClass);

    const carouselItems = carouselRef.current.children;

    const el = carouselRef.current.getElementsByClassName(css.isRef)[0];
    el.classList.remove(css.isRef);

    let newItem = nextElement(el);
    newItem.classList.add(css.isRef);
    newItem.style.order = 1;

    for (let i = 2; i <= carouselItems.length; i++) {
      newItem = nextElement(newItem);
      newItem.style.order = i;
    }

    carouselRef.current.classList.remove(css.isSet);

    setTimeout(() => {
      carouselRef.current.classList.add(css.isSet);
    }, 50);
  }, [isReversingClass]);

  const onClickBack = useCallback(() => {
    carouselRef.current.classList.remove(isReversingClass);

    const carouselItems = carouselRef.current.children;

    const el = carouselRef.current.getElementsByClassName(css.isRef)[0];
    el.classList.remove(css.isRef);

    let newItem = prevElement(el);
    newItem.classList.add(css.isRef);
    newItem.style.order = 1;

    for (let i = carouselItems.length; i >= 2; i--) {
      newItem = prevElement(newItem);
      newItem.style.order = i;
    }

    carouselRef.current.classList.remove(css.isSet);

    setTimeout(() => {
      carouselRef.current.classList.add(css.isSet);
    }, 50);
  }, [isReversingClass]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    slickNext: onClickNext,
    slickPrev: onClickBack,
  }));

  useEffect(() => {
    if (isSliderEnable) {
      carouselRef.current.classList.add(css.carousel, carouselClass, css.isSet);

      const lastElement = carouselRef.current.firstElementChild;
      lastElement.classList.add(css.isRef);
    } else {
      carouselRef.current.classList.add(css.carousel, carouselClass, css.disableCarousel);
    }
  }, [isSliderEnable, carouselClass]);

  return (
  <div className={classes} style={{ '--column-size': `calc(100% / ${activeColumns})` }}>
      {isSliderEnable && showArrows && (
        <div className={css.buttonWrapper}>
          <button
            onClick={onClickBack}
            className={classNames(css.arrowButton, { [css.extendedArrowLeft]: extended })}
          >
            <IconArrowHead direction="left" size="small" className={css.iconArrow} />
          </button>
        </div>
      )}
      <div
        className={classNames(css.wrapper, contentClassName, { [css.marginAuto]: !isSliderEnable })}
      >
        <div className={css.items} ref={carouselRef} data-carousel-items>
          {React.Children.map(children, (child, index) => (
            <div key={index} className={itemClass}>
              {child}
            </div>
          ))}
        </div>
      </div>
      {isSliderEnable && showArrows && (
        <div className={css.buttonWrapper}>
          <button
            onClick={onClickNext}
            className={classNames(css.arrowButton, { [css.extendedArrowRight]: extended })}
          >
            <IconArrowHead direction="right" size="small" className={css.iconArrow} />
          </button>
        </div>
      )}
    </div>
  );
});

Carousel.defaultProps = {
  rootClassName: null,
  className: null,
  contentClassName: null,
  columns: 3,
  useSlider: true,
  showArrows: true,
  responsive: null,
};

Carousel.propTypes = {
  rootClassName: string,
  className: string,
  contentClassName: string,
  columns: number,
  useSlider: bool,
  showArrows: bool,
};

export default Carousel;
